import React from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Eye, Lock, UserCheck, Mail, Phone } from 'lucide-react';
import { businessInfo } from '../data/mock';

const PrivacyPolicy = () => {
  const sections = [
    {
      icon: Eye,
      title: "Information We Collect",
      content: [
        "When you use our contact form or request a quote, we collect:",
        "• Your name and contact details (email, phone number)",
        "• Project details and service requirements",
        "• Any additional information you choose to provide"
      ]
    },
    {
      icon: Shield,
      title: "How We Use Your Information",
      content: [
        "We use your personal information to:",
        "• Respond to your inquiries and quote requests",
        "• Provide tipper hire services",
        "• Communicate about your projects",
        "• Improve our services and customer experience",
        "• Send service updates (only if you've opted in)"
      ]
    },
    {
      icon: Lock,
      title: "Data Protection & Security",
      content: [
        "We take your privacy seriously:",
        "• Your data is stored securely and never sold to third parties",
        "• We use industry-standard security measures",
        "• Access to your information is limited to authorized personnel only",
        "• We retain your data only as long as necessary to provide our services"
      ]
    },
    {
      icon: UserCheck,
      title: "Your Rights",
      content: [
        "You have the right to:",
        "• Access the personal information we hold about you",
        "• Request correction of inaccurate information",
        "• Request deletion of your personal data",
        "• Opt-out of marketing communications at any time",
        "• Lodge a complaint with the relevant data protection authority"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Hero Section */}
      <section className="relative py-20 bg-zinc-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/50 mb-4">Legal</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-300">
              Last updated: December 2025
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-zinc-800 border-zinc-700 mb-8">
              <CardContent className="p-8">
                <p className="text-gray-300 leading-relaxed mb-4">
                  At Adelaide Hills Tipper Hire, we respect your privacy and are committed to protecting your personal information. 
                  This Privacy Policy explains how we collect, use, and safeguard your data when you use our website and services.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  By using our website or services, you agree to the collection and use of information in accordance with this policy.
                </p>
              </CardContent>
            </Card>

            {/* Main Sections */}
            <div className="space-y-6">
              {sections.map((section, index) => (
                <Card key={index} className="bg-zinc-800 border-zinc-700 hover:border-orange-500 transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <section.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-white font-bold text-2xl mb-4">{section.title}</h2>
                        <div className="space-y-2">
                          {section.content.map((line, idx) => (
                            <p key={idx} className="text-gray-300 leading-relaxed">
                              {line}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Cookies */}
            <Card className="bg-zinc-800 border-zinc-700 hover:border-orange-500 transition-all duration-300 mt-6">
              <CardContent className="p-8">
                <h2 className="text-white font-bold text-2xl mb-4">Cookies & Tracking</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Our website may use cookies to enhance your browsing experience. Cookies are small text files stored on your device 
                  that help us understand how you use our site.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  You can control cookie settings through your browser preferences. Note that disabling cookies may affect some 
                  functionality of our website.
                </p>
              </CardContent>
            </Card>

            {/* Third-Party Services */}
            <Card className="bg-zinc-800 border-zinc-700 hover:border-orange-500 transition-all duration-300 mt-6">
              <CardContent className="p-8">
                <h2 className="text-white font-bold text-2xl mb-4">Third-Party Services</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We may use third-party services to help operate our website and provide our services. These third parties 
                  have access to your information only to perform specific tasks on our behalf and are obligated not to disclose 
                  or use it for any other purpose.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  We are not responsible for the privacy practices of third-party websites linked from our site.
                </p>
              </CardContent>
            </Card>

            {/* Updates */}
            <Card className="bg-zinc-800 border-zinc-700 hover:border-orange-500 transition-all duration-300 mt-6">
              <CardContent className="p-8">
                <h2 className="text-white font-bold text-2xl mb-4">Updates to This Policy</h2>
                <p className="text-gray-300 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
                  Privacy Policy on this page and updating the "Last updated" date at the top of this document.
                </p>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="bg-gradient-to-br from-orange-600 to-orange-500 border-0 mt-8">
              <CardContent className="p-8 text-center">
                <h2 className="text-white font-bold text-2xl mb-4">Questions About Privacy?</h2>
                <p className="text-white/90 mb-6">
                  If you have any questions about this Privacy Policy or how we handle your data, please contact us:
                </p>
                <div className="flex flex-wrap justify-center gap-6 text-white">
                  <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-2 hover:text-white/80 transition-colors">
                    <Mail className="w-5 h-5" />
                    {businessInfo.email}
                  </a>
                  <a href={`tel:${businessInfo.phone}`} className="flex items-center gap-2 hover:text-white/80 transition-colors">
                    <Phone className="w-5 h-5" />
                    {businessInfo.phone}
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
